import type { FC } from 'react'

import { Icon, Input, Radio, RadioGroup, Tooltip } from '@skytech/ui'
import type { FieldProps } from 'formik'
import { Field } from 'formik'
import InfoVariantCircleOutlineIcon from 'mdi-react/InfoVariantCircleOutlineIcon'

import { useTranslation } from '@/app/translations/hooks'
import Text from '@/app/translations/Text'

import { useOnboardingForm } from '../hooks'

import type { OnboardingFormFields } from './types'

export const SimCardType: FC<{
  isEditable: boolean
  simType: OnboardingFormFields['simType']
  eid: OnboardingFormFields['eid']
  simCardNo: OnboardingFormFields['simCardNo']
}> = ({ isEditable, simType, eid, simCardNo }) => {
  const translate = useTranslation()
  const { data } = useOnboardingForm()

  return (
    <section>
      <h3>
        <Text id="Sim Card" />
      </h3>

      <RadioGroup id="simType">
        <Field name="simType">
          {({ field, form }: FieldProps) => (
            <Radio
              id="physicalSimCard"
              label={<Text id="I need a physical sim card" />}
              marginBottom={16}
              isDisabled={!isEditable}
              {...field}
              checked={field.value === 'physical'}
              onChange={() => form.setFieldValue('simType', 'physical')}
            />
          )}
        </Field>

        <Field name="simType">
          {({ field, meta, form }: FieldProps) => (
            <Radio
              id="existing"
              label={<Text id="I have a sim card" />}
              marginBottom={16}
              isDisabled={!isEditable}
              error={meta.error}
              touched={meta.touched}
              {...field}
              checked={field.value === 'existing'}
              onChange={() => form.setFieldValue('simType', 'existing')}
            />
          )}
        </Field>

        <Field name="simCardNo">
          {({ field, meta, form }: FieldProps) => {
            if (form.values.simType !== 'existing') return
            return (
              <Input
                wrapperClassName="mt-4"
                id="simCardNo"
                marginBottom={16}
                label={
                  <div className="flex justify-between">
                    <div>
                      <Text id="Sim card number" /> ({simCardNo?.toString()?.length}/20)
                    </div>
                  </div>
                }
                error={meta.error}
                touched={meta.touched}
                isDisabled={!isEditable}
                maxLength={20}
                {...field}
              />
            )
          }}
        </Field>

        {data?.allowEsim && (
          <Field name="simType">
            {({ field, meta, form }: FieldProps) => (
              <Radio
                id="eSim"
                label={<Text id="I want to use eSim" />}
                marginBottom={16}
                isDisabled={!isEditable}
                error={meta.error}
                touched={meta.touched}
                {...field}
                checked={field.value === 'eSim'}
                onChange={() => form.setFieldValue('simType', 'eSim')}
              />
            )}
          </Field>
        )}

        <Field name="simType">
          {({ field, form }: FieldProps) => (
            <Radio
              id="notSure"
              label={<Text id="I don't know" />}
              marginBottom={16}
              isDisabled={!isEditable}
              {...field}
              checked={field.value === 'not_sure'}
              onChange={() => form.setFieldValue('simType', 'not_sure')}
            />
          )}
        </Field>
      </RadioGroup>

      {((simType === 'eSim' && isEditable) || (!isEditable && eid)) && (
        <Field name="eid">
          {({ field, meta }: FieldProps) => (
            <Input
              wrapperClassName="mt-4"
              id="eid"
              label={
                <div className="flex justify-between">
                  <div>
                    <Text id="EID number" /> ({eid?.length}/32)
                  </div>
                  <Tooltip id="eid-info" size="large" />
                  <Icon
                    colorMode="info"
                    isWhiteLabeled={false}
                    data-tooltip-id="eid-info"
                    data-tooltip-content={translate('eid-tooltip-info')}
                  >
                    <InfoVariantCircleOutlineIcon />
                  </Icon>
                </div>
              }
              error={meta.error}
              touched={meta.touched}
              isDisabled={!isEditable}
              maxLength={32}
              {...field}
            />
          )}
        </Field>
      )}
    </section>
  )
}
